import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { Plus, Trash2, Building2, X, Save, AlertCircle, Users, Edit } from "lucide-react";

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingC, setEditingC] = useState(null);
  const [editingU, setEditingU] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([api.get("/admin/companies"), api.get("/admin/users")]);
      setCompanies(c.data);
      setUsers(u.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveCompany = async () => {
    setError(null);
    try {
      const payload = {
        name: editingC.name,
        depot_address: editingC.depot_address,
        depot_lat: editingC.depot_lat ?? null,
        depot_lng: editingC.depot_lng ?? null,
        contact_email: editingC.contact_email || null,
        contact_phone: editingC.contact_phone || null,
        margin_percent: editingC.margin_percent === "" || editingC.margin_percent == null ? 17.647 : Number(editingC.margin_percent),
      };
      if (editingC.id) await api.put(`/admin/companies/${editingC.id}`, payload);
      else await api.post("/admin/companies", payload);
      setEditingC(null);
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const saveUser = async () => {
    setError(null);
    try {
      const payload = {
        email: editingU.email,
        password: editingU.password || "",
        name: editingU.name,
        role: editingU.role,
        company_id: editingU.company_id || null,
        phone: editingU.phone || null,
        admin_pin: editingU.admin_pin || null,
      };
      if (editingU.id) await api.put(`/admin/users/${editingU.id}`, payload);
      else {
        const { data } = await api.post("/admin/users", payload);
        if (data.attached_existing) window.alert("Ce compte existait déjà : il a été rattaché à cette entreprise (multi-entreprises). Son mot de passe et son PIN restent inchangés.");
      }
      setEditingU(null);
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const userCompanyIds = (u) => u.company_ids && u.company_ids.length ? u.company_ids : (u.company_id ? [u.company_id] : []);
  const usersByCompany = (cid) => users.filter((u) => userCompanyIds(u).includes(cid));

  const removeUserFromCompany = async (u, cid) => {
    const multi = userCompanyIds(u).length > 1;
    if (multi) {
      if (!window.confirm(`Détacher ${u.name} de cette entreprise ? Le compte restera rattaché à ses autres entreprises.`)) return;
      await api.delete(`/admin/users/${u.id}/companies/${cid}`);
    } else {
      if (!window.confirm("Supprimer cet utilisateur ?")) return;
      await api.delete(`/admin/users/${u.id}`);
    }
    load();
  };

  return (
    <div data-testid="admin-companies-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="lc-small-label mb-1">Back-office</div>
          <h1 className="display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <Building2 className="w-7 h-7 text-[var(--lc-orange)]" />
            Entreprises &amp; Utilisateurs
          </h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditingC({ name: "", depot_address: "", contact_email: "", contact_phone: "", margin_percent: 17.647 })} data-testid="admin-add-company" className="lc-btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" />Entreprise</button>
          <button onClick={() => setEditingU({ email: "", password: "", name: "", role: "client", company_id: companies[0]?.id || "", phone: "" })} data-testid="admin-add-user" className="lc-btn-secondary inline-flex items-center gap-2"><Plus className="w-4 h-4" />Utilisateur</button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading && <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>}

      <div className="grid gap-3" data-testid="companies-list">
        {companies.map((c) => (
          <div key={c.id} data-testid={`company-row-${c.id}`} className="lc-card p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-[var(--lc-gray)] mt-1">Dépôt : {c.depot_address}</div>
                <div className="text-xs text-[var(--lc-gray)] mt-0.5">Marge client : {Number(c.margin_percent ?? 17.647).toLocaleString("fr-FR")}%</div>
                {c.contact_email && <div className="text-xs text-[var(--lc-gray)]">{c.contact_email} · {c.contact_phone}</div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditingC({ ...c })} data-testid={`company-edit-${c.id}`} className="px-3 py-1.5 text-xs font-semibold border border-[var(--lc-border)] rounded hover:border-[var(--lc-black)] inline-flex items-center gap-1.5"><Edit className="w-3 h-3" />Modifier</button>
                <button onClick={async () => { if (window.confirm("Supprimer cette entreprise (et ses utilisateurs) ?")) { await api.delete(`/admin/companies/${c.id}`); load(); } }} className="px-3 py-1.5 text-xs font-semibold border border-red-300 text-red-600 rounded"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[var(--lc-border)]">
              <div className="lc-small-label flex items-center gap-1.5 mb-2"><Users className="w-3 h-3" />Utilisateurs ({usersByCompany(c.id).length})</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {usersByCompany(c.id).map((u) => (
                  <div key={u.id} className="bg-[var(--lc-surface)] p-2 rounded flex items-center justify-between text-xs">
                    <div>
                      <div className="font-semibold text-sm">{u.name}{userCompanyIds(u).length > 1 && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider text-[var(--lc-orange)] bg-[var(--lc-orange-light)] px-1 py-0.5 rounded align-middle">Multi-entreprises</span>}</div>
                      <div className="text-[var(--lc-gray)]">{u.email}</div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setEditingU({ ...u, password: "" })} className="p-1.5 hover:bg-black/5 rounded"><Edit className="w-3 h-3" /></button>
                      <button data-testid={`remove-user-${u.id}-${c.id}`} onClick={() => removeUserFromCompany(u, c.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
                {usersByCompany(c.id).length === 0 && <div className="text-xs text-[var(--lc-gray)] italic">Aucun utilisateur</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Admins list (no company) */}
      <div className="mt-6">
        <div className="lc-small-label mb-2">Administrateurs</div>
        <div className="lc-card p-4">
          {users.filter((u) => u.role === "admin").map((u) => (
            <div key={u.id} className="flex items-center justify-between text-sm py-1">
              <div><span className="font-semibold">{u.name}</span> · <span className="text-[var(--lc-gray)]">{u.email}</span></div>
              <button onClick={() => setEditingU({ ...u, password: "" })} className="text-xs text-[var(--lc-orange)]">Modifier</button>
            </div>
          ))}
        </div>
      </div>

      {/* Company editor */}
      {editingC && (
        <Modal title={editingC.id ? "Modifier l'entreprise" : "Nouvelle entreprise"} onClose={() => setEditingC(null)} onSave={saveCompany}>
          <div className="space-y-3">
            <div><label className="lc-label">Nom</label><input data-testid="company-name" className="lc-input" value={editingC.name} onChange={(e) => setEditingC({ ...editingC, name: e.target.value })} /></div>
            <div>
              <label className="lc-label">Adresse du dépôt</label>
              <AddressAutocomplete
                testId="company-depot-address"
                value={editingC.depot_address}
                onChange={(v) => setEditingC({ ...editingC, depot_address: v })}
                onSelect={({ formatted_address, lat, lng }) => setEditingC({ ...editingC, depot_address: formatted_address, depot_lat: lat, depot_lng: lng })}
                placeholder="Adresse du dépôt..."
              />
              <p className="text-[11px] text-[var(--lc-gray)] mt-1">Utilisée comme point de départ pour le calcul de distance.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="lc-label">Email contact</label><input data-testid="company-email" type="email" className="lc-input" value={editingC.contact_email || ""} onChange={(e) => setEditingC({ ...editingC, contact_email: e.target.value })} /></div>
              <div><label className="lc-label">Téléphone</label><input data-testid="company-phone" className="lc-input" value={editingC.contact_phone || ""} onChange={(e) => setEditingC({ ...editingC, contact_phone: e.target.value })} /></div>
            </div>
            <div>
              <label className="lc-label">Marge client (% HT)</label>
              <input data-testid="company-margin" type="number" step="0.001" min="0" className="lc-input" value={editingC.margin_percent ?? 17.647} onChange={(e) => setEditingC({ ...editingC, margin_percent: e.target.value })} />
              <p className="text-[11px] text-[var(--lc-gray)] mt-1">Appliquée aux prix affichés côté entreprise (vendeurs et admin). Vos prix de vente restent inchangés dans le back-office. Ex : 17,647% → 130€ HT devient 183,53€ TTC.</p>
            </div>
          </div>
        </Modal>
      )}

      {/* User editor */}
      {editingU && (
        <Modal title={editingU.id ? "Modifier l'utilisateur" : "Nouvel utilisateur"} onClose={() => setEditingU(null)} onSave={saveUser}>
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="lc-label">Nom complet</label><input data-testid="user-name" className="lc-input" value={editingU.name} onChange={(e) => setEditingU({ ...editingU, name: e.target.value })} /></div>
              <div><label className="lc-label">Email</label><input data-testid="user-email" type="email" className="lc-input" value={editingU.email} onChange={(e) => setEditingU({ ...editingU, email: e.target.value })} /></div>
              <div><label className="lc-label">Mot de passe {editingU.id && "(laisser vide pour ne pas changer)"}</label><input data-testid="user-password" type="text" className="lc-input" value={editingU.password || ""} onChange={(e) => setEditingU({ ...editingU, password: e.target.value })} /></div>
              <div><label className="lc-label">Téléphone</label><input data-testid="user-phone" className="lc-input" value={editingU.phone || ""} onChange={(e) => setEditingU({ ...editingU, phone: e.target.value })} /></div>
              {editingU.role !== "admin" && (
                <div>
                  <label className="lc-label">PIN admin entreprise (4 chiffres){editingU.id && " — vide = inchangé"}</label>
                  <input data-testid="user-admin-pin" inputMode="numeric" maxLength={4} className="lc-input font-mono" value={editingU.admin_pin || ""} onChange={(e) => setEditingU({ ...editingU, admin_pin: e.target.value.replace(/\D/g, "") })} placeholder="Réinitialiser le PIN" />
                </div>
              )}
              <div>
                <label className="lc-label">Rôle</label>
                <select data-testid="user-role" className="lc-input" value={editingU.role} onChange={(e) => setEditingU({ ...editingU, role: e.target.value })}>
                  <option value="client">Client</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              {editingU.role !== "admin" && (
                <div>
                  <label className="lc-label">Entreprise</label>
                  <select data-testid="user-company" className="lc-input" value={editingU.company_id || ""} onChange={(e) => setEditingU({ ...editingU, company_id: e.target.value })}>
                    <option value="">— Sélectionner —</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, onSave, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch md:items-center justify-center p-0 md:p-6">
      <div className="bg-white w-full md:max-w-2xl md:max-h-[90vh] overflow-auto md:rounded-md">
        <div className="sticky top-0 bg-white border-b border-[var(--lc-border)] px-5 py-3 flex items-center justify-between z-10">
          <div className="font-bold">{title}</div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
        <div className="sticky bottom-0 bg-white border-t border-[var(--lc-border)] px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="lc-btn-secondary">Annuler</button>
          <button onClick={onSave} data-testid="modal-save" className="lc-btn-primary inline-flex items-center gap-2"><Save className="w-4 h-4" />Enregistrer</button>
        </div>
      </div>
    </div>
  );
}
